/**
 * Estructura unificada de error de la API (ApiErrorResponse).
 * La API devuelve: { message, errors?, detail? }
 */
export interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
  detail?: string;
}

/**
 * Extrae el mensaje de error de una respuesta de la API para mostrarlo al usuario.
 * Soporta la estructura unificada { message, errors?, detail? } y formatos legacy:
 * - JSON con { message } o { mensaje }
 * - Cuerpo en texto plano (string)
 * - ModelState de ASP.NET Core con { message, errors }
 */
export function getApiErrorMessage(
  error: unknown,
  defaultMessage: string = 'Ha ocurrido un error'
): string {
  if (error == null) return defaultMessage;

  const data = (error as { response?: { data?: unknown } })?.response?.data;

  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    
    // Manejar ModelState de ASP.NET Core (errores de validación)
    // Formato: { "propertyName": ["error message 1", "error message 2"], ... }
    // o { "errors": { "propertyName": ["error message"] } }
    if (obj.errors && typeof obj.errors === 'object' && !Array.isArray(obj.errors)) {
      const modelStateErrors = obj.errors as Record<string, unknown>;
      const errorMessages: string[] = [];
      
      for (const key in modelStateErrors) {
        const value = modelStateErrors[key];
        if (Array.isArray(value)) {
          errorMessages.push(...value.filter(v => typeof v === 'string'));
        } else if (typeof value === 'string') {
          errorMessages.push(value);
        }
      }
      
      if (errorMessages.length > 0) {
        return errorMessages.join('. ');
      }
    }
    
    // Manejar ModelState directo (sin wrapper "errors")
    // Buscar propiedades que sean arrays de strings (típico de ModelState)
    const possibleModelStateErrors: string[] = [];
    for (const key in obj) {
      const value = obj[key];
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
        possibleModelStateErrors.push(...value);
      }
    }
    if (possibleModelStateErrors.length > 0) {
      return possibleModelStateErrors.join('. ');
    }
    
    // Estructura unificada: message (API normalizada) | mensaje (legacy)
    const msg =
      obj.message ??
      obj.mensaje ??
      obj.Message ??
      obj.Mensaje ??
      obj.detail ??
      obj.error ??
      obj.title;
    if (msg != null && typeof msg === 'string' && msg.trim()) {
      return msg.trim();
    }
    if (Array.isArray(obj.errors) && obj.errors.length > 0) {
      const first = obj.errors[0];
      if (typeof first === 'string') return first;
      if (first && typeof first === 'object' && 'message' in first) {
        return String((first as { message?: string }).message ?? defaultMessage);
      }
    }
  }

  const message = (error as Error)?.message;
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }

  return defaultMessage;
}
